import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Box,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  History as HistoryIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config/config';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  searchInput: {
    flexGrow: 1,
  },
  incomeChip: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  expenseChip: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  statsCard: {
    marginBottom: theme.spacing(2),
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  filterItem: {
    minWidth: 120,
  },
}));

const api = axios.create({
  baseURL: config.API.BASE_URL,
  timeout: config.API.TIMEOUT,
});

// Secret key for API authentication (matches backend config)
const SECRET_KEY = config.SECURITY.SECRET_KEY;

const CoinHistoryPage = () => {
  const classes = useStyles();
  const [coinHistory, setCoinHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Filters
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedIsIncome, setSelectedIsIncome] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Stats
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalExpense: 0,
    netBalance: 0,
    totalTransactions: 0
  });

  useEffect(() => {
    fetchCoinHistory();
  }, [page, rowsPerPage, selectedUserId, selectedType, selectedIsIncome, startDate, endDate]);

  const fetchCoinHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['key'] = SECRET_KEY;
      
      const params = {
        page: page + 1, // Backend expects 1-based pagination
        limit: rowsPerPage,
      };
      
      if (selectedUserId) params.userId = selectedUserId;
      if (selectedType !== '') params.type = selectedType;
      if (selectedIsIncome !== '') params.isIncome = selectedIsIncome === 'true';
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await api.get('/coin_history/all', { params });
      
      if (response.data.status) {
        setCoinHistory(response.data.data || []);
        setTotalRecords(response.data.pagination?.totalRecords || 0);
        calculateStats(response.data.data || []);
      } else {
        toast.error('Error loading coin history');
      }
    } catch (error) {
      console.error('Error fetching coin history:', error);
      toast.error('Error loading coin history');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (historyData) => {
    const totalIncome = historyData
      .filter(item => item.is_income)
      .reduce((sum, item) => sum + (item.coin || 0), 0);
    
    const totalExpense = historyData
      .filter(item => !item.is_income)
      .reduce((sum, item) => sum + (item.coin || 0), 0);
    
    setStats({
      totalIncome,
      totalExpense,
      netBalance: totalIncome - totalExpense,
      totalTransactions: historyData.length
    });
  };

  const handleRefresh = () => {
    fetchCoinHistory();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSelectedUserId('');
    setSelectedType('');
    setSelectedIsIncome('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const getTransactionTypeText = (type) => {
    const typeMap = {
      0: 'Admin',
      1: 'Purchase',
      2: 'Trainer',
      3: 'Create',
      4: 'Edit',
      5: 'Upscaler',
      6: 'Generate'
    };
    return typeMap[type] || 'Unknown';
  };

  const getTransactionTypeChip = (type) => {
    const typeMap = {
      0: { label: 'Admin', color: '#9c27b0' },
      1: { label: 'Purchase', color: '#4caf50' },
      2: { label: 'Trainer', color: '#ff9800' },
      3: { label: 'Create', color: '#2196f3' },
      4: { label: 'Edit', color: '#673ab7' },
      5: { label: 'Upscaler', color: '#009688' },
      6: { label: 'Generate', color: '#e91e63' }
    };
    
    const typeInfo = typeMap[type] || { label: 'Unknown', color: '#757575' };
    
    return (
      <Chip 
        label={typeInfo.label} 
        size="small" 
        style={{ backgroundColor: typeInfo.color, color: 'white' }}
      />
    );
  };

  const getPaymentGatewayText = (gateway) => {
    return gateway === 0 ? 'Google Play Store' : 'Apple App Store';
  };

  const getIncomeChip = (isIncome) => {
    return isIncome ? 
      <Chip 
        label="Income" 
        className={classes.incomeChip} 
        size="small" 
        icon={<IncomeIcon />}
      /> :
      <Chip 
        label="Expense" 
        className={classes.expenseChip} 
        size="small" 
        icon={<ExpenseIcon />}
      />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        <HistoryIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Coin History
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Income
              </Typography>
              <Typography variant="h5" style={{ color: '#4caf50' }}>
                {stats.totalIncome.toLocaleString()} coins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Expense
              </Typography>
              <Typography variant="h5" style={{ color: '#f44336' }}>
                {stats.totalExpense.toLocaleString()} coins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Net Balance
              </Typography>
              <Typography 
                variant="h5" 
                style={{ color: stats.netBalance >= 0 ? '#4caf50' : '#f44336' }}
              >
                {stats.netBalance.toLocaleString()} coins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Transactions
              </Typography>
              <Typography variant="h5">
                {totalRecords.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper className={classes.paper}>
        {/* Filters */}
        <Box className={classes.filterContainer}>
          <TextField
            className={classes.filterItem}
            variant="outlined"
            size="small"
            label="User ID"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
          />
          
          <FormControl variant="outlined" size="small" className={classes.filterItem}>
            <InputLabel>Transaction Type</InputLabel>
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              label="Transaction Type"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="0">Admin</MenuItem>
              <MenuItem value="1">Purchase</MenuItem>
              <MenuItem value="2">Trainer</MenuItem>
              <MenuItem value="3">Create</MenuItem>
              <MenuItem value="4">Edit</MenuItem>
              <MenuItem value="5">Upscaler</MenuItem>
              <MenuItem value="6">Generate</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" className={classes.filterItem}>
            <InputLabel>Direction</InputLabel>
            <Select
              value={selectedIsIncome}
              onChange={(e) => setSelectedIsIncome(e.target.value)}
              label="Direction"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Income</MenuItem>
              <MenuItem value="false">Expense</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            className={classes.filterItem}
            variant="outlined"
            size="small"
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          
          <TextField
            className={classes.filterItem}
            variant="outlined"
            size="small"
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          
          <Button
            variant="outlined"
            onClick={clearFilters}
            size="small"
          >
            Clear Filters
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>

        {/* Coin History Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell>Coin Plan</TableCell>
                <TableCell>Payment Gateway</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Coins</TableCell>
                <TableCell>Dollar</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading coin history...
                  </TableCell>
                </TableRow>
              ) : coinHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No coin history found
                  </TableCell>
                </TableRow>
              ) : (
                coinHistory.map((history) => (
                  <TableRow key={history.id}>
                    <TableCell>{history.user_id || 'N/A'}</TableCell>
                    <TableCell>{history.coin_plan_id || 'N/A'}</TableCell>
                    <TableCell>
                      {history.payment_gateway !== null && history.payment_gateway !== undefined 
                        ? getPaymentGatewayText(history.payment_gateway) 
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{formatDate(history.date)}</TableCell>
                    <TableCell>{getIncomeChip(history.is_income)}</TableCell>
                    <TableCell>
                      <strong style={{ color: history.is_income ? '#4caf50' : '#f44336' }}>
                        {history.is_income ? '+' : '-'}{history.coin || 0}
                      </strong>
                    </TableCell>
                    <TableCell>{formatCurrency(history.dollar)}</TableCell>
                    <TableCell>{getTransactionTypeChip(history.type)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export default CoinHistoryPage;